import React, { useState, useEffect } from 'react';
import { Loading, Title } from 'react-admin';
import {
  Paper, TextField, Button, Checkbox, FormControlLabel, IconButton,
  List, ListItem, ListItemText, ListItemSecondaryAction,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import dataProvider from '../../../providers/dataProvider';
import ScreenDescription from '../ScreenDescription';

// Configuração do relatório mensal automático: dia do envio, liga/desliga, e
// os telefones que recebem. Não é um Resource — é uma tela única (singleton
// pro setting + lista simples pros telefones), então usa dataProvider direto
// em vez de <List>/<Edit>, igual whatsappMessages/WhatsappMessagesList.js.
const ReportSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [setting, setSetting] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [newPhone, setNewPhone] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [settingResult, recipientsResult] = await Promise.all([
        dataProvider('GET_LIST', 'whatsapp_report_setting', { filter: {}, pagination: {}, sort: {} }),
        dataProvider('GET_LIST', 'whatsapp_report_recipient', { filter: {}, pagination: {}, sort: { field: 'phone', order: 'ASC' } }),
      ]);
      setSetting(settingResult.data[0]);
      setRecipients(recipientsResult.data);
    } catch (err) {
      alert('Não foi possível carregar a configuração do relatório.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const saveSetting = async () => {
    setLoading(true);
    try {
      await dataProvider('UPDATE', 'whatsapp_report_setting', { id: setting.id, data: setting });
      await fetchAll();
    } catch (err) {
      alert('Não foi possível salvar.');
      setLoading(false);
    }
  };

  const addPhone = async () => {
    if (!newPhone) return;
    setLoading(true);
    try {
      await dataProvider('CREATE', 'whatsapp_report_recipient', { data: { phone: newPhone, active: true } });
      setNewPhone('');
      await fetchAll();
    } catch (err) {
      alert('Não foi possível adicionar o telefone.');
      setLoading(false);
    }
  };

  const removePhone = async (id, phone) => {
    if (!window.confirm(`Remover o telefone ${phone} do relatório mensal?`)) {
      return;
    }
    setLoading(true);
    try {
      await dataProvider('DELETE', 'whatsapp_report_recipient', { id });
      await fetchAll();
    } catch (err) {
      alert('Não foi possível remover o telefone.');
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 600 }}>
      <Title title="Relatório mensal de WhatsApp" />
      <ScreenDescription>
        Envia automaticamente, todo mês, um resumo da volumetria (enviadas, recebidas, custo por bloco) do mês anterior pros
        telefones cadastrados abaixo. Roda todo dia, mas só dispara de fato no dia do mês configurado — mudar o dia ou desligar
        aqui não precisa de deploy.
      </ScreenDescription>
      {loading && <Loading />}
      {!loading && setting && (
        <>
          <Paper style={{ padding: 24, marginBottom: 24 }} elevation={1}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 500 }}>Envio automático</h2>
            <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={setting.enabled}
                    onChange={e => setSetting({ ...setting, enabled: e.target.checked })}
                    color="primary"
                  />
                }
                label="Ligado"
              />
              <TextField
                label="Dia do envio"
                type="number"
                value={setting.sendDay}
                onChange={e => setSetting({ ...setting, sendDay: e.target.value })}
                inputProps={{ min: 1, max: 31 }}
                style={{ width: 120 }}
              />
              <Button variant="contained" color="primary" onClick={saveSetting}>Salvar</Button>
            </div>
          </Paper>

          <Paper style={{ padding: 24 }} elevation={1}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 500 }}>Telefones cadastrados</h2>
            <List dense>
              {recipients.map(r => (
                <ListItem key={r.id} disableGutters>
                  <ListItemText primary={r.phone} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => removePhone(r.id, r.phone)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {recipients.length === 0 && (
                <ListItem disableGutters>
                  <ListItemText primary="Nenhum telefone cadastrado" style={{ color: '#999' }} />
                </ListItem>
              )}
            </List>
            <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'flex-end' }}>
              <TextField
                label="Telefone"
                placeholder="+55..."
                value={newPhone}
                onChange={e => setNewPhone(e.target.value)}
                style={{ flex: 1 }}
              />
              <Button variant="outlined" color="primary" onClick={addPhone}>Adicionar</Button>
            </div>
          </Paper>
        </>
      )}
    </div>
  );
};

export default ReportSettingsPage;
